import { Box, Center, Image, Stack } from '@mantine/core';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { login } from '@/api/auth';
import { usePageTitle } from '@/components/usePageTitle';
import { LoginForm } from '@/features/auth/LoginForm';
import { forest } from '@/theme/theme';

/** Only accept a same-origin path (starts with a single `/`), never a protocol-relative or absolute URL. */
function isSafeRedirect(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//');
}

export const Route = createFileRoute('/login')({
  validateSearch: (s: Record<string, unknown>): { redirect?: string } => ({
    redirect: isSafeRedirect(s.redirect) ? s.redirect : undefined,
  }),
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation();
  usePageTitle(t('auth.signIn'));
  const { auth } = Route.useRouteContext();
  const { redirect } = Route.useSearch();
  const navigate = useNavigate();

  const handleSubmit = async (email: string, password: string) => {
    const me = await login(email, password);
    auth.setMe(me);
    await navigate({ to: redirect ?? '/' });
  };

  return (
    <Box pos="relative" h="100vh" bg={forest[9]} style={{ overflow: 'hidden' }}>
      <Box
        pos="absolute"
        inset={0}
        style={{
          backgroundImage: 'url(/rastr.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.28,
        }}
      />
      <Center pos="relative" h="100%">
        <Stack align="center" gap="xl">
          <Image src="/logo-white.png" alt="Forestum" w={160} fit="contain" />
          <LoginForm onSubmit={handleSubmit} />
        </Stack>
      </Center>
    </Box>
  );
}
